/**
 * Roll Up Hold ホバーアニメーション
 *
 * 対象要素のテキストを2コピーのスロット構造でラップし、ホバー/フォーカスで
 * 上方向にロールさせる。離脱時はその位置で固定（latched）し、再ホバーで
 * 先頭に戻ってから再生する。
 *
 * 既存のHTML構造には手を加えず、このモジュールを外せば元の見た目に戻る。
 */

const READY_FLAG = 'rollHoldReady';
const RESET_CLASS = 'is-hold-reset';
const ACTIVE_CLASS = 'is-hold-active';
const LATCHED_CLASS = 'is-hold-latched';

const holdFrames = new WeakMap();

function cancelHoldFrames(el) {
  const frames = holdFrames.get(el);
  if (!frames) return;
  frames.forEach((frame) => cancelAnimationFrame(frame));
  holdFrames.delete(el);
}

export function wrap(el) {
  if (!el || el.dataset[READY_FLAG] === '1') return;

  const text = (el.textContent || '').trim();
  if (!text) return;

  el.textContent = '';

  const outer = document.createElement('span');
  outer.className = 'fx-roll-outer';

  const track = document.createElement('span');
  track.className = 'fx-roll-track';

  const copy1 = document.createElement('span');
  copy1.className = 'fx-roll-copy';
  copy1.textContent = text;

  const copy2 = document.createElement('span');
  copy2.className = 'fx-roll-copy';
  copy2.setAttribute('aria-hidden', 'true');
  copy2.textContent = text;

  track.append(copy1, copy2);
  outer.append(track);
  el.append(outer);

  el.classList.add('fx-roll-up-hold');
  el.dataset[READY_FLAG] = '1';
}

/**
 * `wrap` の逆処理。スロット構造を解いて元のプレーンテキストへ戻す。
 * `.fx-roll-outer` は overflow:hidden でマスクをかけるため、適用を外す際は
 * クラス除去だけでなくテキストの復元まで行わないと見切れが残る。
 */
export function unwrap(el) {
  if (!el || el.dataset[READY_FLAG] !== '1') return;

  cancelHoldFrames(el);

  const copy = el.querySelector('.fx-roll-copy');
  const text = copy ? copy.textContent : (el.textContent || '');
  el.textContent = text;

  el.classList.remove(
    'fx-roll-up-hold',
    RESET_CLASS,
    ACTIVE_CLASS,
    LATCHED_CLASS,
  );
  delete el.dataset[READY_FLAG];
}

export function replayHold(el) {
  cancelHoldFrames(el);
  el.classList.remove(ACTIVE_CLASS, LATCHED_CLASS);
  el.classList.add(RESET_CLASS);
  void el.offsetWidth;

  const resetFrame = requestAnimationFrame(() => {
    el.classList.remove(RESET_CLASS);

    const activeFrame = requestAnimationFrame(() => {
      el.classList.add(ACTIVE_CLASS);
      holdFrames.delete(el);
    });

    holdFrames.set(el, [activeFrame]);
  });

  holdFrames.set(el, [resetFrame]);
}

export function latchHold(el) {
  if (
    !el.classList.contains(RESET_CLASS) &&
    !el.classList.contains(ACTIVE_CLASS) &&
    !el.classList.contains(LATCHED_CLASS)
  ) {
    return;
  }

  cancelHoldFrames(el);
  el.classList.remove(RESET_CLASS, ACTIVE_CLASS);
  el.classList.add(LATCHED_CLASS);
}

const HOVER_SELECTOR = [
  '.header-logo',
  '.header-nav-list a',
  '.header-lang-other',
].join(', ');

// SP（≤700px）ではホバーが使えず、タップ時の focus でアニメーションが
// 暴発するため、ヘッダー要素への自動適用（ラップ＋イベント登録）を行わない。
const spQuery = window.matchMedia('(max-width: 700px)');

// 登録したイベントをまとめて解除できるよう AbortController で束ねる。
let hoverController = null;

function enableHover() {
  if (hoverController) return;

  hoverController = new AbortController();
  const { signal } = hoverController;

  document.querySelectorAll(HOVER_SELECTOR).forEach((el) => {
    wrap(el);
    el.addEventListener('mouseenter', () => replayHold(el), { signal });
    el.addEventListener('mouseleave', () => latchHold(el), { signal });
    el.addEventListener('focus', () => {
      if (el.matches(':focus-visible')) replayHold(el);
    }, { signal });
    el.addEventListener('blur', () => latchHold(el), { signal });
  });
}

function disableHover() {
  if (hoverController) {
    hoverController.abort();
    hoverController = null;
  }
  document.querySelectorAll(HOVER_SELECTOR).forEach((el) => unwrap(el));
}

// 初期化＋リサイズで PC/SP の閾値をまたぐたびに切り替える。
function syncHover() {
  if (spQuery.matches) disableHover();
  else enableHover();
}

syncHover();
spQuery.addEventListener('change', syncHover);
